import {
  MINIMUM_DERIVED_PROFILE,
  type Profile,
  higherProfile,
  profileRank,
} from './profiles';

export type ClassificationAnswer = {
  id: string;
  label: string;
  /** The level this answer triggers; undefined when it feeds other rules only. */
  level?: Profile;
  /** Words used in the "because …" reasons. */
  reason?: string;
};

export type ClassificationQuestion = {
  id: string;
  question: string;
  answers: readonly ClassificationAnswer[];
  /** What the answer feeds when it sets no level. */
  note?: string;
};

/** T01 lite (journey spec §4.1). Changing these is a contract version bump. */
export const CLASSIFICATION_QUESTIONS: readonly ClassificationQuestion[] = [
  {
    id: 'change',
    question: 'What kind of work is this?',
    note: 'A change to an existing system adds S08',
    answers: [
      { id: 'greenfield', label: 'New application' },
      { id: 'existing', label: 'Change to an existing system' },
    ],
  },
  {
    id: 'data',
    question: 'Highest data classification',
    answers: [
      { id: 'public', label: 'Public', level: 'P1' },
      { id: 'internal', label: 'Internal', level: 'P1' },
      { id: 'confidential', label: 'Confidential', level: 'P2', reason: 'confidential data' },
      { id: 'critical', label: 'High / critical', level: 'P3', reason: 'high or critical data' },
    ],
  },
  {
    id: 'pii',
    question: 'Personal data',
    answers: [
      { id: 'none', label: 'None', level: 'P1' },
      { id: 'yes', label: 'Yes', level: 'P2', reason: 'personal data' },
      { id: 'special', label: 'Special category', level: 'P3', reason: 'special category personal data' },
    ],
  },
  {
    id: 'crit',
    question: 'Who depends on it?',
    answers: [
      { id: 'internal', label: 'Internal users', level: 'P1' },
      { id: 'customers', label: 'Customers', level: 'P2', reason: 'customers depend on it' },
      { id: 'critical', label: 'A critical or important function', level: 'P3', reason: 'supports a critical or important function' },
    ],
  },
  {
    id: 'rev',
    question: 'Can it be undone if something goes wrong?',
    answers: [
      { id: 'easy', label: 'Easily rolled back', level: 'P1' },
      { id: 'partly', label: 'Partly', level: 'P2', reason: 'only partly reversible' },
      { id: 'permanent', label: 'Permanent effects', level: 'P3', reason: 'permanent effects' },
    ],
  },
  {
    id: 'ext',
    question: 'Effects outside Vivicta',
    answers: [
      { id: 'none', label: 'None', level: 'P1' },
      { id: 'notify', label: 'Messages to customers or partners', level: 'P2', reason: 'sends messages to customers or partners' },
      { id: 'legal', label: 'Payments or legal effect', level: 'P3', reason: 'payments or legal effect' },
    ],
  },
  {
    id: 'priv',
    question: 'Does it need to read or change other live systems?',
    note: "Other systems, like the CRM, ERP or user accounts. Its own database doesn't count.",
    answers: [
      { id: 'none', label: 'No, only its own data', level: 'P1' },
      { id: 'read', label: 'Reads other systems', level: 'P2', reason: 'reads other live systems' },
      { id: 'write', label: 'Changes other systems or accounts', level: 'P3', reason: 'changes other live systems' },
    ],
  },
  {
    id: 'reg',
    question: 'Do laws or the contract set extra rules for what it does?',
    note: 'For example banking, insurance or health rules, or rules for AI. Normal privacy rules like GDPR are covered later, in Regulatory screening.',
    answers: [
      { id: 'none', label: 'No, only the usual rules', level: 'P1' },
      { id: 'sector', label: "The client's industry is regulated, but not this function", level: 'P2', reason: "the client's industry is regulated" },
      { id: 'critical', label: 'This function itself is regulated', level: 'P3', reason: 'the function itself is regulated' },
    ],
  },
  {
    id: 'ai',
    question: 'Can AI agents help build it?',
    note: 'AI agents can draft documents, code and tests; people always review and decide. You agree how much they do with the client later.',
    answers: [
      { id: 'yes', label: 'Yes, most of it' },
      { id: 'partly', label: 'Some of it, like documents and tests' },
      { id: 'no', label: 'No, people do all of it' },
    ],
  },
  {
    id: 'aisys',
    question: 'Will the finished application use AI?',
    note: "For example a chatbot, or a model that sorts, scores or summarises data. This is about what users get, not how it's built.",
    answers: [
      { id: 'no', label: 'No' },
      { id: 'yes', label: 'Yes, it uses AI' },
    ],
  },
];

export type ClassificationAnswers = Readonly<Record<string, string | undefined>>;

const questionById = new Map(CLASSIFICATION_QUESTIONS.map(q => [q.id, q]));

export const answerOf = (questionId: string, answerId: string | undefined) =>
  questionById.get(questionId)?.answers.find(answer => answer.id === answerId);

export const unansweredQuestions = (answers: ClassificationAnswers) =>
  CLASSIFICATION_QUESTIONS.filter(q => !answerOf(q.id, answers[q.id]));

export type DerivedProfile = { profile: Profile; reasons: string[] };

/**
 * The profile is the highest level any answer triggers, never below P1
 * (ASDP-PRF-01). Reasons name only the answers that set that level.
 */
export function deriveProfile(answers: ClassificationAnswers): DerivedProfile {
  const triggered = CLASSIFICATION_QUESTIONS.flatMap(q => {
    const answer = answerOf(q.id, answers[q.id]);
    return answer?.level ? [answer] : [];
  });
  const profile = triggered.reduce<Profile>(
    (highest, answer) => higherProfile(highest, answer.level!),
    MINIMUM_DERIVED_PROFILE,
  );
  const reasons = triggered
    .filter(answer => answer.level === profile && answer.reason)
    .map(answer => answer.reason!);
  return { profile, reasons };
}

/** Choosing a profile below the derived one needs a reason (ASDP-PRF-02). */
export const isLowering = (derived: Profile, chosen: Profile) =>
  profileRank(chosen) < profileRank(derived);

export const hasPersonalData = (answers: ClassificationAnswers) =>
  answers.pii !== undefined && answers.pii !== 'none';
